<template>
  <ContentWrap>
    <el-collapse v-model="activeNames">
      <el-collapse-item name="basicInfo">
        <template #title>
          <span class="text-base font-bold">基本信息</span>
        </template>
        <el-descriptions :column="4">
          <el-descriptions-item label="回款编号">{{ receivable.no }}</el-descriptions-item>
          <el-descriptions-item label="客户名称">
            {{ receivable.customerName }}
          </el-descriptions-item>
          <el-descriptions-item label="合同编号">
            {{ receivable.contract?.no }}
          </el-descriptions-item>
          <el-descriptions-item label="回款日期">
            {{ formatDate(receivable.returnTime, 'YYYY-MM-DD') }}
          </el-descriptions-item>
          <el-descriptions-item label="回款金额">
            {{ erpPriceInputFormatter(receivable.price) }}
          </el-descriptions-item>
          <el-descriptions-item label="回款方式">
            <dict-tag :type="DICT_TYPE.CRM_RECEIVABLE_RETURN_TYPE" :value="receivable.returnType" />
          </el-descriptions-item>
          <!-- ★ 2026-09-22 新增：原型表单「是否收款付款」下拉。回款自身字段（不是合同属性） -->
          <el-descriptions-item label="是否收款付款">
            <dict-tag
              :type="DICT_TYPE.CRM_RECEIVABLE_RECEIVED_FLAG"
              :value="receivable.receivedFlag"
            />
          </el-descriptions-item>
          <el-descriptions-item label="备注">{{ receivable.remark }}</el-descriptions-item>
        </el-descriptions>
      </el-collapse-item>
      <!-- ★ 2026-09-22 新增：原型表单的「相关文件 + 添加文件」，回款自身的附件；
           无文件时不渲染该面板，避免详情页出现空壳。 -->
      <el-collapse-item v-if="fileList.length > 0" name="fileInfo">
        <template #title>
          <span class="text-base font-bold">相关文件</span>
        </template>
        <el-descriptions :column="1">
          <el-descriptions-item label="相关文件">
            <el-link
              v-for="(url, index) in fileList"
              :key="index"
              :href="url"
              class="mb-5px block"
              target="_blank"
              type="primary"
            >
              {{ fileName(url) }}
            </el-link>
          </el-descriptions-item>
        </el-descriptions>
      </el-collapse-item>
      <el-collapse-item name="systemInfo">
        <template #title>
          <span class="text-base font-bold">系统信息</span>
        </template>
        <el-descriptions :column="4">
          <el-descriptions-item label="负责人">
            {{ receivable.ownerUserName }}
          </el-descriptions-item>
          <el-descriptions-item label="创建人">
            {{ receivable.creatorName }}
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">
            {{ formatDate(receivable.createTime) }}
          </el-descriptions-item>
          <el-descriptions-item label="更新时间">
            {{ formatDate(receivable.updateTime) }}
          </el-descriptions-item>
        </el-descriptions>
      </el-collapse-item>
    </el-collapse>
  </ContentWrap>
</template>
<script setup lang="ts">
import * as ReceivableApi from '@/api/crm/receivable'
import { DICT_TYPE } from '@/utils/dict'
import { formatDate } from '@/utils/formatTime'
import { erpPriceInputFormatter } from '@/utils'

const { receivable } = defineProps<{
  receivable: ReceivableApi.ReceivableVO
}>()

// 展示的折叠面板
const activeNames = ref(['basicInfo', 'systemInfo'])

/** 相关文件（url 逗号串）→ 数组 */
const fileList = computed(() =>
  (receivable.relatedFiles ?? '')
    .split(',')
    .map((url) => url.trim())
    .filter((url) => url.length > 0)
)

/** 从 url 里取文件名做展示文案（取不到就退回原始 url） */
const fileName = (url: string) => {
  const name = url.split('/').pop()?.split('?')[0]
  if (!name || name.length === 0) {
    return url
  }
  // ★ 必须 decodeURIComponent：后端 formatFileUrl 用 UriUtils.encodePathSegment 编码路径，
  //   中文文件名在 url 里是 %E4%BA%91...，不解码会把编码串直接显示给用户。
  try {
    return decodeURIComponent(name)
  } catch {
    return name
  }
}
</script>
